/**
 * KLARHEIM Main JavaScript — v1.2 (Production)
 * Accordion · Cart Drawer · Mobile Nav · Sticky ATC · Header scroll
 *
 * Security: no innerHTML for user/merchant data; all DOM built via createElement.
 * Accessibility: focus trapping, inert background, ARIA lifecycle fully managed.
 * No jQuery. Vanilla ES2020.
 */

'use strict';

/* ═══════════════════════════════════════════════════════════════════════════
   UTILITIES
   ═══════════════════════════════════════════════════════════════════════════ */

function qs(selector, scope) {
  return (scope || document).querySelector(selector);
}

function qsa(selector, scope) {
  return Array.from((scope || document).querySelectorAll(selector));
}

/**
 * Trap focus within an element. Returns a cleanup function.
 * Queries focusable elements fresh on each Tab keydown so dynamically-added
 * nodes (like qty buttons) are always included.
 */
function trapFocus(element) {
  function getFocusable() {
    return Array.from(element.querySelectorAll(
      'a[href]:not([disabled]):not([tabindex="-1"]), ' +
      'button:not([disabled]):not([tabindex="-1"]), ' +
      'input:not([disabled]):not([tabindex="-1"]), ' +
      'select:not([disabled]):not([tabindex="-1"]), ' +
      'textarea:not([disabled]):not([tabindex="-1"]), ' +
      '[tabindex]:not([tabindex="-1"])'
    ));
  }

  function handleKeydown(e) {
    if (e.key !== 'Tab') return;
    const focusable = getFocusable();
    if (!focusable.length) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }

  element.addEventListener('keydown', handleKeydown);
  // Move focus into the element — first focusable item
  const firstFocusable = getFocusable()[0];
  firstFocusable?.focus();

  return () => element.removeEventListener('keydown', handleKeydown);
}

/** Format euro amount from Shopify cents */
function formatMoney(cents) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' })
    .format(cents / 100);
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE NAV
   ═══════════════════════════════════════════════════════════════════════════ */

class MobileNav {
  constructor() {
    this.nav    = qs('#MobileNav');
    this.toggle = qs('.hamburger');
    this.isOpen = false;
    this.releaseFocus   = null;
    this._previousFocus = null;

    if (!this.nav || !this.toggle) return;

    // Keep the off-canvas nav out of the focus tree until opened.
    this.nav.setAttribute('aria-hidden', 'true');
    this.nav.setAttribute('inert', '');

    this.toggle.addEventListener('click', () => this.onToggle());
    qs('.mobile-nav__close', this.nav)?.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });
  }

  onToggle() { this.isOpen ? this.close() : this.open(); }

  open() {
    this._previousFocus = document.activeElement;
    this.isOpen = true;
    this.nav.classList.add('is-open');
    this.nav.removeAttribute('aria-hidden');
    this.nav.removeAttribute('inert');
    this.toggle.classList.add('is-open');
    this.toggle.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Background becomes inert — screen readers and keyboard cannot leave the nav
    qs('#MainContent')?.setAttribute('inert', '');
    qs('.site-header')?.setAttribute('inert', '');
    qs('.site-footer')?.setAttribute('inert', '');
    this.releaseFocus = trapFocus(this.nav);
  }

  close() {
    this.isOpen = false;
    this.nav.classList.remove('is-open');
    this.nav.setAttribute('aria-hidden', 'true');
    this.nav.setAttribute('inert', '');
    this.toggle.classList.remove('is-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    qs('#MainContent')?.removeAttribute('inert');
    qs('.site-header')?.removeAttribute('inert');
    qs('.site-footer')?.removeAttribute('inert');
    this.releaseFocus?.();
    this.releaseFocus = null;
    (this._previousFocus || this.toggle)?.focus();
    this._previousFocus = null;
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   HEADER SCROLL
   ═══════════════════════════════════════════════════════════════════════════ */

class HeaderScroll {
  constructor() {
    this.header = qs('.site-header');
    if (!this.header) return;
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.onScroll();
  }

  onScroll() {
    this.header.classList.toggle('is-scrolled', window.scrollY > 32);
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   FAQ ACCORDION — native <details>/<summary>, JS for exclusive-open behaviour
   ═══════════════════════════════════════════════════════════════════════════ */

class FaqAccordion {
  constructor(container) {
    this.items = qsa('details.faq-item', container);
    this.items.forEach(item => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        this.items.forEach(other => {
          if (other !== item && other.open) other.removeAttribute('open');
        });
      });
    });
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   CART DRAWER — all DOM built with createElement (no innerHTML for data)
   ═══════════════════════════════════════════════════════════════════════════ */

class CartDrawer {
  constructor() {
    this.drawer   = qs('#CartDrawer');
    this.body     = qs('#CartDrawerBody');
    this.footer   = this.drawer?.querySelector('.cart-drawer__footer');
    this.overlay  = this.drawer?.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.drawer?.querySelector('.cart-drawer__close');
    this.isOpen   = false;
    this.releaseFocus   = null;
    this._previousFocus = null;

    if (!this.drawer) return;

    // Initialise properly hidden
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');

    this.closeBtn?.addEventListener('click', () => this.close());
    this.overlay?.addEventListener('click',  () => this.close());
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    qsa('[data-cart-open]').forEach(el => {
      el.addEventListener('click', (e) => { e.preventDefault(); this.open(); });
    });
  }

  open() {
    this._previousFocus = document.activeElement;
    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.drawer.removeAttribute('aria-hidden');
    this.drawer.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    qs('#MainContent')?.setAttribute('inert', '');
    qs('.site-header')?.setAttribute('inert', '');
    qs('.site-footer')?.setAttribute('inert', '');
    qs('#MobileNav')?.setAttribute('inert', '');
    this.releaseFocus = trapFocus(this.drawer);
    this.refresh();
  }

  close() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.drawer.setAttribute('inert', '');
    document.body.style.overflow = '';
    qs('#MainContent')?.removeAttribute('inert');
    qs('.site-header')?.removeAttribute('inert');
    qs('.site-footer')?.removeAttribute('inert');
    qs('#MobileNav')?.setAttribute('inert', '');
    this.releaseFocus?.();
    this.releaseFocus = null;
    this._previousFocus?.focus();
    this._previousFocus = null;
  }

  async refresh() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      this.render(cart);
      this.updateBadge(cart.item_count);
    } catch (e) {
      console.error('[KLARHEIM] Cart refresh failed:', e);
    }
  }

  /** Build cart DOM safely — no innerHTML for dynamic data */
  render(cart) {
    if (!this.body) return;
    this.body.textContent = ''; // clear safely
    const isEmpty = cart.item_count === 0;

    if (this.footer) {
      this.footer.hidden = isEmpty;
    }

    // Keep the drawer subtotal in sync with the live cart total (dynamic, no hardcoded price).
    const subtotalEl = this.drawer.querySelector('[data-cart-subtotal]');
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);

    if (isEmpty) {
      const msg = document.createElement('p');
      msg.className = 'cart-drawer__empty';
      msg.textContent = 'Dein Warenkorb ist leer.';
      this.body.appendChild(msg);

      // Restrained "continue shopping" ghost CTA — plain navigation, no cart logic.
      const cta = document.createElement('a');
      cta.className = 'btn btn--ghost cart-drawer__empty-cta';
      cta.href = '/';
      cta.textContent = 'Weiter einkaufen';
      this.body.appendChild(cta);

      if (this.isOpen) {
        this.releaseFocus?.();
        this.releaseFocus = trapFocus(this.drawer);
      }
      return;
    }

    cart.items.forEach(item => {
      this.body.appendChild(this._buildItem(item));
    });

    // Re-trap focus after DOM rebuild so new focusable elements are in scope
    if (this.isOpen) {
      this.releaseFocus?.();
      this.releaseFocus = trapFocus(this.drawer);
    }
  }

  _buildItem(item) {
    const wrap = document.createElement('div');
    wrap.className = 'cart-item';
    wrap.dataset.line = item.key;

    if (item.image) {
      const img = document.createElement('img');
      img.src       = item.image;
      img.alt       = item.title;
      img.width     = 80;
      img.height    = 80;
      img.className = 'cart-item__image';
      img.loading   = 'lazy';
      img.decoding  = 'async';
      wrap.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'cart-item__info';

    const title = document.createElement('span');
    title.className   = 'cart-item__title';
    title.textContent = item.product_title;
    info.appendChild(title);

    if (item.variant_title) {
      const variant = document.createElement('span');
      variant.className   = 'cart-item__variant';
      variant.textContent = item.variant_title;
      info.appendChild(variant);
    }

    const row = document.createElement('div');
    row.className = 'cart-item__row';

    const qtyWrap = document.createElement('div');
    qtyWrap.className = 'cart-item__qty';
    qtyWrap.setAttribute('role', 'group');
    qtyWrap.setAttribute('aria-label', `Menge für ${item.product_title}`);

    const decBtn = this._mkQtyBtn('−', 'decrease', item.key, `Menge verringern für ${item.product_title}`);
    const qtyVal = document.createElement('span');
    qtyVal.className   = 'qty-value';
    qtyVal.textContent = String(item.quantity);
    qtyVal.setAttribute('aria-live', 'polite');
    const incBtn = this._mkQtyBtn('+', 'increase', item.key, `Menge erhöhen für ${item.product_title}`);
    qtyWrap.append(decBtn, qtyVal, incBtn);

    const price = document.createElement('span');
    price.className   = 'cart-item__price';
    price.textContent = formatMoney(item.final_line_price);

    const removeBtn = document.createElement('button');
    removeBtn.type      = 'button';
    removeBtn.className = 'cart-item__remove';
    removeBtn.setAttribute('aria-label', `${item.product_title} entfernen`);
    // Static SVG — no user data; safe innerHTML use
    removeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>';

    row.append(qtyWrap, price, removeBtn);
    info.appendChild(row);
    wrap.appendChild(info);

    decBtn.addEventListener('click', () => this._changeQty(item.key, item.quantity, 'decrease'));
    incBtn.addEventListener('click', () => this._changeQty(item.key, item.quantity, 'increase'));
    removeBtn.addEventListener('click', () => this._updateLine(item.key, 0));

    return wrap;
  }

  _mkQtyBtn(label, action, lineKey, ariaLabel) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'qty-btn';
    btn.textContent = label;
    btn.dataset.action = action;
    btn.dataset.line   = lineKey;
    btn.setAttribute('aria-label', ariaLabel);
    return btn;
  }

  async _changeQty(lineKey, currentQty, direction) {
    const qty = direction === 'increase' ? currentQty + 1 : Math.max(0, currentQty - 1);
    await this._updateLine(lineKey, qty);
  }

  async _updateLine(id, quantity) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      if (!res.ok) throw new Error('cart/change failed');
      const cart = await res.json();
      this.render(cart);
      this.updateBadge(cart.item_count);
    } catch (e) {
      console.error('[KLARHEIM] Cart update failed:', e);
    }
  }

  updateBadge(count) {
    qsa('.cart-count-badge').forEach(el => {
      el.textContent  = String(count);
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   ADD TO CART — delegates to CartDrawer.open() which fetches cart internally.
   Do NOT call cartDrawer.refresh() before open() — open() already refreshes.
   ═══════════════════════════════════════════════════════════════════════════ */

class AddToCart {
  constructor(cartDrawer) {
    this.cartDrawer = cartDrawer;
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-atc]');
      if (!btn) return;
      e.preventDefault();
      this._add(btn);
    });
  }

  async _add(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId || btn.dataset.busy === 'true') return;

    const quantityInput = qs('#ProductQuantity');
    const quantityValue = Number.parseInt(quantityInput?.value || '1', 10);
    const quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
    const originalText = btn.textContent.trim();
    const relatedButtons = qsa(`[data-atc][data-variant-id="${variantId}"]`);
    const status = qs('#ProductAtcStatus');
    const error = qs('#ProductAtcError');
    btn.dataset.busy = 'true';
    btn.textContent = '…';
    relatedButtons.forEach(button => {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    });
    if (status) status.textContent = 'Artikel wird hinzugefügt …';
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity })
      });
      if (!res.ok) throw new Error('ATC failed');
      btn.textContent = 'Hinzugefügt';
      if (status) status.textContent = 'Artikel wurde zum Warenkorb hinzugefügt.';
      // Give the live region time to announce before open() makes MainContent inert.
      await new Promise(resolve => setTimeout(resolve, 300));
      // open() handles the refresh internally — do not call refresh() separately.
      this.cartDrawer.open();
    } catch (e) {
      console.error('[KLARHEIM] Add to cart failed:', e);
      btn.textContent = 'Fehler — erneut versuchen';
      if (status) status.textContent = '';
      if (error) {
        error.textContent = 'Der Artikel konnte nicht hinzugefügt werden. Bitte versuchen Sie es erneut.';
        error.hidden = false;
      }
    } finally {
      setTimeout(() => {
        relatedButtons.forEach(button => {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        });
        delete btn.dataset.busy;
        btn.textContent = originalText;
        if (status) status.textContent = '';
      }, 2000);
    }
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   STICKY ATC
   Observes the primary ATC button. Works on:
     - Homepage: [data-sticky-atc-trigger] on the hero CTA
     - PDP: #ProductAtcBtn (the main product form's submit button)
   When bar is hidden: aria-hidden="true" + inert + tabindex="-1" on child btn
   When bar is visible: aria-hidden removed + inert removed + tabindex="0"
   ═══════════════════════════════════════════════════════════════════════════ */

class StickyAtc {
  constructor() {
    this.bar = qs('#StickyAtc');
    if (!this.bar) return;

    this.stickyBtn = this.bar.querySelector('[data-atc]');
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.primaryPassed = false;
    this.footerVisible = false;

    // Start fully hidden before resolving triggers.
    this._hide();
    this.mobileQuery.addEventListener('change', () => this._sync());

    // Trigger priority: PDP ATC button → hero CTA trigger → nothing
    const trigger =
      qs('#ProductAtcBtn') ||
      qs('[data-sticky-atc-trigger]');

    if (!trigger) return;

    const observer = new IntersectionObserver(([entry]) => {
      this.primaryPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this._sync();
    }, { threshold: 0 });

    observer.observe(trigger);

    const footer = qs('.site-footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        this._sync();
      }, { threshold: 0, rootMargin: '0px 0px 76px 0px' });
      footerObserver.observe(footer);
    }
  }

  _sync() {
    if (this.mobileQuery.matches && this.primaryPassed && !this.footerVisible) this._show();
    else this._hide();
  }

  _show() {
    this.bar.classList.add('is-visible');
    this.bar.removeAttribute('aria-hidden');
    this.bar.removeAttribute('inert');
    if (this.stickyBtn) this.stickyBtn.removeAttribute('tabindex');
  }

  _hide() {
    this.bar.classList.remove('is-visible');
    this.bar.setAttribute('aria-hidden', 'true');
    this.bar.setAttribute('inert', '');
    if (this.stickyBtn) this.stickyBtn.setAttribute('tabindex', '-1');
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   NEWSLETTER FORM — inline validation, Shopify customer form
   ═══════════════════════════════════════════════════════════════════════════ */

class NewsletterForm {
  constructor(form) {
    this.form   = form;
    this.input  = form.querySelector('input[type="email"]');
    this.submit = form.querySelector('[type="submit"]');
    this.result = form.querySelector('.newsletter-form__result');
    form.addEventListener('submit', (e) => this.onSubmit(e));
  }

  async onSubmit(e) {
    e.preventDefault();
    const email = this.input?.value?.trim();
    if (!email || !email.includes('@')) {
      this._showResult('Bitte gib eine gültige E-Mail-Adresse ein.', false);
      return;
    }
    if (this.submit) this.submit.disabled = true;
    try {
      const data = new FormData(this.form);
      data.append('form_type', 'customer');
      data.append('utf8', '✓');
      const res = await fetch('/contact#contact_form', {
        method: 'POST',
        body: data,
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        this._showResult('Danke! Du bist jetzt dabei.', true);
        this.form.reset();
      } else {
        throw new Error();
      }
    } catch {
      this._showResult('Ein Fehler ist aufgetreten. Bitte versuche es erneut.', false);
    } finally {
      if (this.submit) this.submit.disabled = false;
    }
  }

  _showResult(msg, success) {
    if (!this.result) return;
    this.result.textContent = msg;
    this.result.dataset.success = success ? 'true' : 'false';
    this.result.style.display = 'block';
  }
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAZY IMAGE POLYFILL — only loads if native lazy loading is absent
   ═══════════════════════════════════════════════════════════════════════════ */

function initLazyImages() {
  if ('loading' in HTMLImageElement.prototype) return;
  const images = qsa('img[loading="lazy"]');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      const img = entry.target;
      if (img.dataset.src) img.src = img.dataset.src;
      observer.unobserve(img);
    });
  });
  images.forEach(img => observer.observe(img));
}

/* ═══════════════════════════════════════════════════════════════════════════
   INIT
   ═══════════════════════════════════════════════════════════════════════════ */

document.addEventListener('DOMContentLoaded', () => {
  new HeaderScroll();
  new MobileNav();

  const cartDrawer = new CartDrawer();
  new AddToCart(cartDrawer);
  new StickyAtc();

  qsa('.faq-list').forEach(el => new FaqAccordion(el));
  qsa('.newsletter-form').forEach(el => new NewsletterForm(el));

  initLazyImages();
});
